import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ExternalLink, LogIn, RefreshCw, ChevronDown, ChevronUp, ChevronRight, History, ArrowLeft } from 'lucide-react';
import {
  Button,
  Badge,
  InShellPanel,
  InShellPanelHeader,
  InShellPanelTitle,
  InShellPanelDescription,
  Separator,
} from '@staff-app/ui';
import type { CustomerResolveResult, CustomerInfo, CustomerInfoAuditEntry, CustomerNote } from '@staff-app/shared-types';
import {
  useStaffGetCustomerInfo,
  useStaffGetCustomer360,
  useStaffGetCustomerInfoAuditLog,
  useStaffGetCustomerNotes,
  useStaffAddCustomerNote,
  useStaffUpdateCustomerInfo,
  useStaffLoginAsCustomer,
  useStaffBillingSubscriptionStatus,
} from '../hooks/useStaffCustomer';
import { accountStatus, parseStripeId, parseMaxScreens, parsePaymentFlags, parsePairedScreens, searchedFact } from '../lib/customer-info';
import { useRecentCustomersStore } from '../stores/recent-customers-store';
import { CustomerInfoJsonEditor } from './info-json-editor/CustomerInfoJsonEditor';
import { CustomerFacts } from './CustomerFacts';

const PAGE_SIZE = 5;

function Paginator({ page, total, onPrev, onNext }: { page: number; total: number; onPrev: () => void; onNext: () => void }) {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-1">
      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onPrev} disabled={page === 0}>
        {'← Prev'}
      </Button>
      <span className="text-[10px] text-muted-foreground">{`${page + 1} / ${totalPages}`}</span>
      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onNext} disabled={page >= totalPages - 1}>
        {'Next →'}
      </Button>
    </div>
  );
}

function AuditHistory({ entries }: { entries: CustomerInfoAuditEntry[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  if (!entries.length) return (
    <p className="py-2 text-center text-xs text-muted-foreground">{'No edit history yet.'}</p>
  );
  const pageEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <div className="flex flex-col gap-1">
      {pageEntries.map((entry) => {
        let diffs: [string, { from: unknown; to: unknown }][] = [];
        try { diffs = Object.entries(JSON.parse(entry.changes ?? '{}')); } catch { /* ignore */ }
        const isOpen = expanded === entry._id;
        const date = new Date(entry.changedAt);
        return (
          <div key={entry._id} className="rounded border bg-muted/40 text-xs">
            <button
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/30"
              onClick={() => setExpanded(isOpen ? null : entry._id)}
            >
              {isOpen ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
              <span className="font-medium truncate">{entry.staffEmail}</span>
              <span className="ml-auto shrink-0 text-muted-foreground">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
            {/* Machine writes (OptiClaw) carry a reason — shown collapsed too, so an automated change is
                recognisable at a glance rather than only after expanding the diff. */}
            {entry.reason && (
              <div className="px-3 pb-2 -mt-1 text-[11px] text-muted-foreground break-words">{entry.reason}</div>
            )}
            {isOpen && (
              <div className="border-t px-3 py-2 font-mono text-[11px] space-y-0.5">
                {diffs.length ? diffs.map(([key, { from, to }]) => (
                  <div key={key}>
                    <span className="text-foreground">{key}</span>
                    {': '}
                    <span className="text-destructive">{JSON.stringify(from)}</span>
                    {' → '}
                    <span className="text-success">{JSON.stringify(to)}</span>
                  </div>
                )) : <div className="text-muted-foreground">{'(no detectable changes)'}</div>}
              </div>
            )}
          </div>
        );
      })}
      <Paginator
        page={page}
        total={entries.length}
        onPrev={() => setPage(p => p - 1)}
        onNext={() => setPage(p => p + 1)}
      />
    </div>
  );
}

function NoteCompose({ value, onChange, saving, onSave }: {
  value: string;
  onChange: (v: string) => void;
  saving: boolean;
  onSave: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        className="w-full resize-none rounded border bg-muted px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        rows={3}
        placeholder="Add a note for other agents…"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') onSave(); }}
        spellCheck={false}
      />
      {value.trim() && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{'Ctrl+Enter to save'}</span>
          <Button size="sm" className="h-6 px-2 text-xs" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Note'}
          </Button>
        </div>
      )}
    </div>
  );
}

function NotesList({ notes }: { notes: CustomerNote[] }) {
  const [page, setPage] = useState(0);
  if (!notes.length) return (
    <p className="py-1 text-center text-xs text-muted-foreground">{'No notes yet.'}</p>
  );
  const pageNotes = notes.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <div className="flex flex-col gap-1.5">
      {pageNotes.map((note) => {
        const date = new Date(note.createdAt);
        return (
          <div key={note._id} className="rounded border bg-muted/40 px-3 py-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">{note.staffEmail}</span>
              <span className="text-[10px] text-muted-foreground">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-muted-foreground">{note.text}</p>
          </div>
        );
      })}
      <Paginator
        page={page}
        total={notes.length}
        onPrev={() => setPage(p => p - 1)}
        onNext={() => setPage(p => p + 1)}
      />
    </div>
  );
}

function mergeInfo(info: CustomerInfo): string {
  try {
    return JSON.stringify(
      {
        payment: JSON.parse(info.payment),
        limitation: JSON.parse(info.limitation),
        feature: JSON.parse(info.feature),
      },
      null,
      2,
    );
  } catch {
    return JSON.stringify({ payment: info.payment, limitation: info.limitation, feature: info.feature }, null, 2);
  }
}

interface CustomerPanelProps {
  open: boolean;
  onClose: () => void;
  resolved: CustomerResolveResult | null;
}

export function CustomerPanel({ open, onClose, resolved }: CustomerPanelProps) {
  const accountId = resolved?.accountId ?? null;
  const { data: infoData, loading: infoLoading, refetch: refetchInfo } = useStaffGetCustomerInfo(accountId);
  const { data: customer360Data } = useStaffGetCustomer360(accountId);
  const customer360 = customer360Data?.staffGetCustomer360;
  const { data: auditData, refetch: refetchAudit } = useStaffGetCustomerInfoAuditLog(accountId);
  const auditEntries = auditData?.staffGetCustomerInfoAuditLog ?? [];
  const { data: notesData, refetch: refetchNotes } = useStaffGetCustomerNotes(accountId);
  const notes = notesData?.staffGetCustomerNotes ?? [];
  // Find-in-INFO. The blob is a few hundred lines of JSON, so locating one flag by eye is the
  // slowest part of reading it.
  const [infoQuery, setInfoQuery] = useState('');
  const [infoMatchCount, setInfoMatchCount] = useState(0);
  const [infoMatchIndex, setInfoMatchIndex] = useState(0);
  /** Wraps at both ends, so Enter keeps cycling instead of dead-ending on the last match. */
  const stepMatch = (delta: number) =>
    setInfoMatchIndex((i) =>
      infoMatchCount ? (i + delta + infoMatchCount) % infoMatchCount : 0,
    );

  const [addNoteMutation, { loading: addingNote }] = useStaffAddCustomerNote();
  const [updateInfo] = useStaffUpdateCustomerInfo();
  const [loginMutation, { loading: loginLoading }] = useStaffLoginAsCustomer();

  const info = infoData?.staffGetCustomerInfo;

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Monaco's json worker validates as the operator types; Save stays disabled while broken so
  // the failure is visible at the mistake, not after the click.
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [noteText, setNoteText] = useState('');


  useEffect(() => {
    if (info) {
      setDraft(mergeInfo(info));
      setError(null);
    }
  }, [info]);

  // Track this customer as recently accessed (shown in the Users search empty state).
  const addRecent = useRecentCustomersStore((s) => s.addRecent);
  useEffect(() => {
    if (open && resolved?.accountId) {
      addRecent({
        accountId: resolved.accountId,
        companyName: resolved.companyName,
        ownerEmail: resolved.ownerEmail,
      });
    }
  }, [open, resolved?.accountId, resolved?.companyName, resolved?.ownerEmail, addRecent]);

  const handleLogin = async () => {
    if (!accountId) return;
    try {
      const result = await loginMutation({ variables: { accountId } });
      const appUrl = result.data?.staffLoginAsCustomer?.appUrl;
      if (appUrl) window.open(appUrl, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      toast.error('Login failed', { description: (e as Error)?.message ?? 'Unknown error' });
    }
  };

  const handleRefresh = async () => {
    const result = await refetchInfo();
    const fresh = result.data?.staffGetCustomerInfo;
    if (fresh) {
      setDraft(mergeInfo(fresh));
      setError(null);
    }
  };

  const handleSave = async () => {
    let parsed: { payment?: unknown; limitation?: unknown; feature?: unknown };
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError('Invalid JSON');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateInfo({
        variables: {
          accountId: accountId ?? '',
          patch: {
            payment: JSON.stringify(parsed.payment ?? {}),
            limitation: JSON.stringify(parsed.limitation ?? {}),
            feature: JSON.stringify(parsed.feature ?? {}),
          },
        },
      });
      toast.success('Customer info saved');
      refetchAudit();
    } catch (e: unknown) {
      setError((e as Error)?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const teams = resolved?.teams ?? [];
  const teamLabel =
    teams.length === 0
      ? 'Default Team'
      : teams.map((t) => (t.isCurrent ? `${t.teamName} (active)` : t.teamName)).join(', ');

  const stripeId = parseStripeId(info);
  const subStatus = useStaffBillingSubscriptionStatus(stripeId).data
    ?.staffBillingSubscriptionStatus;
  const maxScreens = parseMaxScreens(info);
  // Paired screens = account-wide: limitation.quantity (the "quantity" field in
  // the info editor below, screens outside any team) + the teams' paired
  // screens — not the raw device count.
  const pairedScreens = parsePairedScreens(info);
  // Status badge: prefer the company `info` blob so it's correct regardless of
  // how the panel was opened (full resolve, device search, or a recents click,
  // none of which carry isTrial/isActive); fall back to the resolve flags until
  // `info` loads.
  const flags = parsePaymentFlags(info);
  const status = accountStatus(
    flags.isTrial ?? resolved?.isTrial,
    flags.isActive ?? resolved?.isActive,
  );

  return (
    <InShellPanel
      open={open}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid="customer-panel"
      className="sm:w-[48vw] sm:max-w-[48vw]"
    >
      {resolved && (
        <div className="flex flex-col gap-5 pt-2 h-full">
          <InShellPanelHeader>
            {/*
              No accountId fallback: the facts block below already carries an "Account id" row
              with its own copy button, and falling back to it printed the same 17 characters
              twice in the same panel. `'Customer'` stays as the last resort so the drawer is
              never untitled — the e2e asserts this title is visible, and an unnamed account
              must not make it disappear.
            */}
            <InShellPanelTitle data-testid="customer-panel-title">
              {resolved.companyName ?? 'Customer'}
            </InShellPanelTitle>
            {/*
              text-xs to match the Users search card, which renders the same customer facts and
              sits right next to this panel. InShellPanelDescription defaults to text-sm, and
              each VALUE here was individually overridden to text-xs — so labels were 14px and
              their values 12px, inconsistent with the card and with each other. Setting it once
              here lets the per-value overrides go, and keeps the two surfaces in step.
            */}
            <InShellPanelDescription className="space-y-0.5 text-xs">
              <CustomerFacts
                prefix="customer-panel"
                status={status}
                plan={resolved.plan}
                planFeature={resolved.planFeature}
                extraBadges={
                  customer360 && customer360.trialExtensionCount > 0 ? (
                    <Badge
                      variant="outline"
                      className="text-[10px]"
                      data-testid="customer-panel-trial-extensions"
                      title={
                        customer360.lastExtendedAt
                          ? `Last extended ${new Date(customer360.lastExtendedAt).toLocaleDateString()}`
                          : undefined
                      }
                    >
                      {`Trial extensions: ${customer360.trialExtensionCount}`}
                    </Badge>
                  ) : null
                }
                ownerEmail={resolved.ownerEmail}
                accountId={resolved.accountId}
                stripeId={stripeId}
                subscriptionStatus={subStatus?.status}
                subscriptionMultiple={subStatus?.multiple}
                pairedScreens={pairedScreens}
                maxScreens={maxScreens}
                searched={searchedFact(resolved)}
                teams={teams.length > 0 ? <span>{teamLabel}</span> : null}
              />
            </InShellPanelDescription>
          </InShellPanelHeader>

          <div className="flex gap-2">
            {/* Login re-points the staff user's own accountId to this account — it
                doesn't impersonate the owner, so it works even with no owner. */}
            {resolved.accountId ? (
              <Button
                className="flex-1 gap-2"
                onClick={handleLogin}
                disabled={loginLoading}
                data-testid="customer-panel-login-btn"
              >
                <LogIn className="h-4 w-4" />
                {loginLoading ? 'Opening…' : 'Login as Customer'}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </Button>
            ) : null}

            {stripeId && (
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeId}`, '_blank', 'noopener,noreferrer')}
                data-testid="customer-panel-stripe-btn"
              >
                <ExternalLink className="h-4 w-4" />
                {'Stripe'}
              </Button>
            )}
          </div>

          <Separator />

          {infoLoading && !info ? (
            <p className="text-center text-xs text-muted-foreground">{'Loading company data…'}</p>
          ) : info ? (
            showHistory ? (
              <div
                className="flex flex-col flex-1 min-h-0 gap-3 overflow-y-auto"
                data-testid="customer-panel-history-view"
              >
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => setShowHistory(false)}>
                    <ArrowLeft className="h-3 w-3" />
                    {'Back'}
                  </Button>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {'History'}
                  </span>
                </div>

                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {'Edit History'}
                </span>
                <AuditHistory entries={auditEntries} />

                <Separator />

                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {'Notes'}
                </span>
                <NoteCompose
                  value={noteText}
                  onChange={setNoteText}
                  saving={addingNote}
                  onSave={async () => {
                    if (!accountId || !noteText.trim()) return;
                    await addNoteMutation({ variables: { accountId, text: noteText.trim() } });
                    setNoteText('');
                    refetchNotes();
                  }}
                />
                <NotesList notes={notes} />
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0 gap-2" data-testid="customer-panel-info">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {'Info'}
                  </span>
                  <div className="flex items-center justify-end gap-1">
                    <div className="relative w-40 shrink-0">
                      <input
                        value={infoQuery}
                        onChange={(e) => {
                          setInfoQuery(e.target.value);
                          setInfoMatchIndex(0);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          // Enter cycles forward, Shift+Enter back — the convention every
                          // find-in-page uses, so it needs no explaining.
                          stepMatch(e.shiftKey ? -1 : 1);
                        }}
                        placeholder="Search info…"
                        aria-label="Search info"
                        spellCheck={false}
                        data-testid="customer-panel-info-search"
                        className="h-6 w-full rounded border bg-muted px-2 pr-12 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                      {infoQuery.trim() && (
                        <span
                          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground"
                          data-testid="customer-panel-info-search-count"
                        >
                          {infoMatchCount ? `${infoMatchIndex + 1}/${infoMatchCount}` : '0/0'}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      disabled={!infoMatchCount}
                      onClick={() => stepMatch(-1)}
                      aria-label="Previous match"
                      data-testid="customer-panel-info-search-prev"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      disabled={!infoMatchCount}
                      onClick={() => stepMatch(1)}
                      aria-label="Next match"
                      data-testid="customer-panel-info-search-next"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 gap-1 px-2 text-xs"
                      onClick={() => setShowHistory(true)}
                      data-testid="customer-panel-history-btn"
                    >
                      <History className="h-3 w-3" />
                      {'History'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={handleRefresh}
                      disabled={infoLoading}
                    >
                      <RefreshCw className={`h-3 w-3 ${infoLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleSave}
                      disabled={saving || jsonErrors.length > 0}
                      title={jsonErrors[0]}
                      data-testid="customer-panel-save-btn"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
                {(error ?? jsonErrors[0]) && (
                  <p className="text-xs text-destructive" data-testid="customer-panel-info-error">
                    {error ?? jsonErrors[0]}
                  </p>
                )}
                <CustomerInfoJsonEditor
                  value={draft}
                  onChange={(v) => { setDraft(v); setError(null); }}
                  query={infoQuery}
                  activeIndex={infoMatchIndex}
                  onMatchesChange={setInfoMatchCount}
                  onValidityChange={setJsonErrors}
                  testId="customer-panel-info-editor"
                />
              </div>
            )
          ) : null}
        </div>
      )}
    </InShellPanel>
  );
}
